<template>
  <div
    :class="{
      'search-box-fi': true,
      'search-box-fi--desktop': isDesktop,
      'search-box-fi--active': isFocused || isSearchSuggestions,
    }"
  >
    <text-input-fi
      :id="id"
      ref="input"
      :model-value="query"
      class="search-box-fi__input no-highlight"
      name="search"
      :label="isDesktop ? $dict.get('search.Placeholder') : undefined"
      :placeholder="isDesktop ? undefined : $dict.get('search.Placeholder')"
      :icon="isDesktop ? 'search' : undefined"
      :clear="isDesktop"
      type="search"
      data-test="search-input"
      white
      @update:model-value="change"
      @focus="onFocus"
      @blur="onBlur"
      @keyup-enter="keyupEnter"
      @clear="clearSearch"
    />
  </div>
</template>

<script setup lang="ts">
import { TextInputFi } from 'atoms';
import { storeToRefs } from 'pinia';
import { computed, ref, useTemplateRef } from 'vue';

import { useSearchLocation } from '@/components/search/searchLocationComposable';
import { useBreakpoints } from '@/composables/breakpoint';
import { useSearchHistory } from '@/composables/searchHistory';
import { useNavigationStore } from '@/store/pinia/navigation';
import { useSearchStore } from '@/store/pinia/search';

defineOptions({
  name: 'SearchBoxFi',
});

const emit = defineEmits<{
  clear: [value: void];
}>();

withDefaults(
  defineProps<{
    isDesktop?: boolean;
    id?: string;
  }>(),
  {
    id: 'search-box',
  },
);

const navigationStore = useNavigationStore();
const searchStore = useSearchStore();
const { query, productRedirectUrl } = storeToRefs(searchStore);
const { getSearchLocation } = useSearchLocation();
const { isSM } = useBreakpoints();
const { setSearchTerm } = useSearchHistory();

const isFocused = ref(false);
const inputRef = useTemplateRef('input');

const isSearchSuggestions = computed(() => navigationStore.showSearchSuggest);
const isSearchPage = computed(() => navigationStore.isSearchPage);

const clearSearch = () => {
  emit('clear');
  setTimeout(() => {
    navigationStore.setShowFlyout(false);
    navigationStore.setShowSearchSuggest(false);
    searchStore.setSearchSuggestions([], '');
  }, 350);
};

const keyupEnter = (queryParam: string) => {
  if (productRedirectUrl.value === null) {
    navigationStore.setShowFlyout(false);
    navigationStore.setShowSearchSuggest(false);
    searchStore.setQuery('');
    return;
  }

  setSearchTerm(queryParam);

  if (productRedirectUrl.value) {
    // This redirect is triggered only if the search query is finished and a redirect url is set.
    // Otherwise the redirect to the product page is triggered within store/search
    window.location.href = productRedirectUrl.value;
    return;
  } else {
    searchStore.setQueryEnter(true);
  }

  const newQuery = queryParam.trim();
  if (isSearchPage.value) {
    navigationStore.setShowFlyout(false);
    navigationStore.setShowSearchSuggest(false);
    searchStore.setSearchSuggestions([], newQuery);
    return;
  }
  if (newQuery === '') {
    return;
  }
  window.location.href = getSearchLocation(newQuery);
};

const focus = () => {
  inputRef.value?.focus();
};

const onFocus = () => {
  if (!isSM.value) {
    return;
  }
  isFocused.value = true;

  if (!searchStore.inhibitSuggestions) {
    navigationStore.setSearchFocused(true);
    if (query.value.trim()) {
      searchStore.setShowSuggestionsFlyout(true);
    }
    searchStore.setQuery(query.value); // make sure suggetions are being loaded
  }
};

const onBlur = () => {
  isFocused.value = false;
  navigationStore.setSearchFocused(false);
};

const change = (value: string) => {
  if (query.value !== value) {
    searchStore.setQuery(value);
  }

  if (value === '') {
    navigationStore.setShowFlyout(false);
    navigationStore.setShowSearchSuggest(false);
    searchStore.setSearchSuggestions([], '');
  }
};

defineExpose({
  focus,
});
</script>

<style scoped src="./search-box-fi" />

<style src="./search-box-fi-unscoped" />
